import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Sparkles } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { API } from '../App';
import { normalizeCategoryImage, normalizeProductImages, toAbsoluteImageUrl } from '../utils/image';

const Home = () => {
  const [categories, setCategories] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);

  useEffect(() => {
    fetchCategories();
    fetchFeaturedProducts();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.map(normalizeCategoryImage));
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchFeaturedProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?featured=true`);
      const normalized = response.data.map(normalizeProductImages);
      setFeaturedProducts(normalized.slice(0, 4));
    } catch (error) {
      console.error('Error fetching featured products:', error);
    }
  };

  return (
    <div data-testid="home-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="hero" data-testid="hero-section">
        <div className="hero-content">
          <div className="hero-badge">
            <Sparkles size={16} />
            <span>Handcrafted with Love</span>
          </div>
          <h1 className="hero-title" data-testid="hero-title">
            Annie's Butterfly Creations
          </h1>
          <p className="hero-subtitle" data-testid="hero-subtitle">
            Unique custom pens, ID holders, and accessories that bring joy to your everyday
          </p>
          <Link to="/shop" className="hero-button" data-testid="hero-shop-button">
            Shop Now
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      {/* Categories Section */}
      <section className="categories-section" data-testid="categories-section">
        <div className="container">
          <h2 className="section-title" data-testid="categories-title">Shop by Category</h2>
          <div className="categories-grid">
                  {categories.map((category) => {
                    const image = toAbsoluteImageUrl(category.image);

                    return (
                    <Link
                      key={category.id}
                      to={`/shop/${category.slug}`}
                      className="category-card"
                      data-testid={`category-${category.slug}`}
                    >
                      <div className="category-image-wrapper">
                        {image ? (
                          <img src={image} alt={category.name} className="category-image" />
                        ) : (
                          <div className="category-placeholder">{category.name[0]}</div>
                        )}
                      </div>
                      <h3 className="category-name">{category.name}</h3>
                      {category.description && (
                        <p className="category-description">{category.description}</p>
                      )}
                    </Link>
                    );
                  })}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      {featuredProducts.length > 0 && (
        <section className="featured-section" data-testid="featured-section">
          <div className="container">
            <h2 className="section-title" data-testid="featured-title">Featured Products</h2>
            <div className="products-grid">
              {featuredProducts.map((product) => {
                const image = toAbsoluteImageUrl(product.images[0]) || 'https://via.placeholder.com/400x300?text=No+Image';

                return (
                <Link
                  key={product.id}
                  to={`/product/${product.slug}`}
                  className="product-card"
                  data-testid={`product-${product.slug}`}
                >
                  <div className="product-image-wrapper">
                    <img src={image} alt={product.name} className="product-image" />
                  </div>
                  <div className="product-info">
                    <h3 className="product-name">{product.name}</h3>
                    <p className="product-price">${product.price.toFixed(2)}</p>
                  </div>
                </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <Footer />

      <style jsx>{`
        .hero {
          margin-top: 80px;
          background: linear-gradient(135deg, rgba(255, 155, 155, 0.1) 0%, rgba(180, 167, 255, 0.1) 50%, rgba(167, 255, 212, 0.1) 100%);
          padding: 6rem 2rem;
          text-align: center;
          position: relative;
          overflow: hidden;
        }

        .hero::before {
          content: '';
          position: absolute;
          top: 15%;
          left: 8%;
          width: 180px;
          height: 180px;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Cg fill='%23A7FFD4' opacity='0.35'%3E%3Cpath d='M100,80 Q90,60 70,55 Q50,50 45,65 Q40,80 50,90 Q60,100 75,95 Q85,92 100,80 Z' /%3E%3Cpath d='M100,80 Q110,60 130,55 Q150,50 155,65 Q160,80 150,90 Q140,100 125,95 Q115,92 100,80 Z' /%3E%3Cpath d='M100,120 Q90,140 70,145 Q50,150 45,135 Q40,120 50,110 Q60,100 75,105 Q85,108 100,120 Z' /%3E%3Cpath d='M100,120 Q110,140 130,145 Q150,150 155,135 Q160,120 150,110 Q140,100 125,105 Q115,108 100,120 Z' /%3E%3Cellipse cx='100' cy='100' rx='8' ry='18' fill='%23A7FFD4'/%3E%3Ccircle cx='100' cy='85' r='6' fill='%23A7FFD4'/%3E%3C/g%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-size: contain;
          pointer-events: none;
          transform: rotate(20deg);
          animation: float 6s ease-in-out infinite;
        }

        .hero::after {
          content: '';
          position: absolute;
          bottom: 10%;
          right: 10%;
          width: 150px;
          height: 150px;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Cg fill='%23FF9B9B' opacity='0.30'%3E%3Cpath d='M100,80 Q90,60 70,55 Q50,50 45,65 Q40,80 50,90 Q60,100 75,95 Q85,92 100,80 Z' /%3E%3Cpath d='M100,80 Q110,60 130,55 Q150,50 155,65 Q160,80 150,90 Q140,100 125,95 Q115,92 100,80 Z' /%3E%3Cpath d='M100,120 Q90,140 70,145 Q50,150 45,135 Q40,120 50,110 Q60,100 75,105 Q85,108 100,120 Z' /%3E%3Cpath d='M100,120 Q110,140 130,145 Q150,150 155,135 Q160,120 150,110 Q140,100 125,105 Q115,108 100,120 Z' /%3E%3Cellipse cx='100' cy='100' rx='8' ry='18' fill='%23FF9B9B'/%3E%3Ccircle cx='100' cy='85' r='6' fill='%23FF9B9B'/%3E%3C/g%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-size: contain;
          pointer-events: none;
          transform: rotate(-25deg);
          animation: float 8s ease-in-out infinite;
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0px) rotate(20deg);
          }
          50% {
            transform: translateY(-20px) rotate(20deg);
          }
        }

        .hero-content {
          max-width: 800px;
          margin: 0 auto;
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: white;
          padding: 0.5rem 1rem;
          border-radius: 50px;
          font-size: 0.85rem;
          font-weight: 500;
          color: #FF9B9B;
          margin-bottom: 2rem;
          box-shadow: 0 2px 10px rgba(255, 155, 155, 0.2);
        }

        .hero-title {
          font-size: 3.5rem;
          font-weight: 700;
          margin-bottom: 1.5rem;
          color: #2D3748;
          line-height: 1.2;
        }

        .hero-subtitle {
          font-size: 1.2rem;
          color: #4A5568;
          margin-bottom: 2.5rem;
          line-height: 1.6;
        }

        .hero-button {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: linear-gradient(135deg, #FF9B9B 0%, #B4A7FF 100%);
          color: white;
          padding: 1rem 2.5rem;
          border-radius: 50px;
          text-decoration: none;
          font-weight: 600;
          font-size: 1rem;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          box-shadow: 0 4px 20px rgba(255, 155, 155, 0.3);
        }

        .hero-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 30px rgba(255, 155, 155, 0.4);
        }

        .categories-section, .featured-section {
          padding: 4rem 2rem;
        }

        .container {
          max-width: 1400px;
          margin: 0 auto;
        }

        .section-title {
          font-size: 2.5rem;
          font-weight: 700;
          text-align: center;
          margin-bottom: 3rem;
          color: #2D3748;
        }

        .categories-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 2rem;
        }

        .category-card {
          background: white;
          border-radius: 20px;
          padding: 2rem;
          text-align: center;
          text-decoration: none;
          color: #2D3748;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
        }

        .category-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 8px 30px rgba(255, 155, 155, 0.2);
        }

        .category-image-wrapper {
          width: 120px;
          height: 120px;
          margin: 0 auto 1.5rem;
          border-radius: 50%;
          overflow: hidden;
          background: linear-gradient(135deg, rgba(255, 155, 155, 0.2) 0%, rgba(180, 167, 255, 0.2) 100%);
        }

        .category-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .category-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 3rem;
          font-weight: 700;
          font-family: 'Playfair Display', serif;
          color: #FF9B9B;
        }

        .category-name {
          font-size: 1.3rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .category-description {
          font-size: 0.9rem;
          color: #718096;
          line-height: 1.5;
        }

        .products-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 2rem;
        }

        .product-card {
          background: white;
          border-radius: 20px;
          overflow: hidden;
          text-decoration: none;
          color: #2D3748;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
        }

        .product-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 8px 30px rgba(255, 155, 155, 0.2);
        }

        .product-image-wrapper {
          width: 100%;
          height: 300px;
          overflow: hidden;
          background: #F7FAFC;
        }

        .product-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        .product-card:hover .product-image {
          transform: scale(1.05);
        }

        .product-info {
          padding: 1.5rem;
        }

        .product-name {
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .product-price {
          font-size: 1.3rem;
          font-weight: 700;
          color: #FF9B9B;
        }

        @media (max-width: 768px) {
          .hero {
            padding: 4rem 1rem;
            margin-top: 60px;
          }

          .hero-title {
            font-size: 2rem;
          }

          .hero-subtitle {
            font-size: 1rem;
          }

          .hero-button {
            padding: 0.875rem 2rem;
            font-size: 0.95rem;
          }

          .section-title {
            font-size: 1.75rem;
            margin-bottom: 2rem;
          }

          .categories-section, .featured-section {
            padding: 3rem 1rem;
          }

          .categories-grid {
            grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
            gap: 1rem;
          }

          .category-card {
            padding: 1.5rem 1rem;
          }

          .category-image-wrapper {
            width: 80px;
            height: 80px;
            margin: 0 auto 1rem;
          }

          .category-name {
            font-size: 1rem;
          }

          .category-description {
            font-size: 0.85rem;
          }

          .products-grid {
            grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
            gap: 1rem;
          }

          .product-image-wrapper {
            height: 180px;
          }

          .product-info {
            padding: 1rem;
          }

          .product-name {
            font-size: 0.95rem;
          }

          .product-price {
            font-size: 1.1rem;
          }
        }
      `}</style>
    </div>
  );
};

export default Home;